import {
	Icon,
	IconButton,
	KeyEventArguments,
	ReorderableList,
	TextInput
} from "client/Components";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { ItemContent } from "./ItemContent";
import { ScopeItem } from "./ScopeItem";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getItemName,
	GroupItemFormModel,
	NodeItemFormModel,
	ScopeItemFormModel,
	ScopesData
} from "./common";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId } from "client/Misc/clientId";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { Key } from "client/Misc/commonTypes";
import { GroupItemMenu } from "./GroupItemMenu";
import styles from "./GroupItem.module.less";
import {
	AutoFocusState,
	useAutoFocus,
	useItemMenuState
} from "client/Hooks";

export type GroupItemContentProps = {
	attachable: IValueInputAttachable<GroupItemFormModel>;
	groups: readonly GroupItemFormModel[];
	autoFocusState: AutoFocusState<Guid>;
	scopesData: ScopesData;
	isSearching: boolean;
	doesMatchSearchTerm: (item: NodeItemFormModel) => boolean;
	onMoveToGroup: (clientId: Guid, targetGroupId: Guid) => void;
	onDeleteGroup: (clientId: Guid) => void;
	onMoveToTop: (clientId: Guid) => void;
	onItemReorder: () => void;
};

export const GroupItem = (props: GroupItemContentProps): ReactElement => {
	const {
		attachable,
		groups,
		autoFocusState,
		scopesData,
		isSearching,
		doesMatchSearchTerm,
		onMoveToGroup,
		onDeleteGroup,
		onMoveToTop,
		onItemReorder
	} = props;

	const {
		groupData
	} = attachable.value;

	const {
		id,
		name,
		scopes
	} = groupData;

	const [isEditMode, setIsEditMode] = useState<boolean>(
		isNil(id)
		&& isNilOrWhiteSpace(name)
	);

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const groupItemClientId: Guid = getClientId(attachable.value);

	const nameAttachable: IValueInputAttachable<string> = attachable
		.for("groupData")
		.for("name");

	const nameValidationState: InputValidationResult = nameAttachable.validation
		.getValidationState();

	const isValid: boolean = nameValidationState.kind === InputValidationResultKind.Valid;

	const errorToShow: string | undefined = nameValidationState.kind !== InputValidationResultKind.Valid
		? nameValidationState.errorMessage
		: undefined;

	const [autoFocus, doAutoFocus] = useAutoFocus<Guid>(groupItemClientId, autoFocusState);

	useEffect(
		() => {
			if (!isValid) {
				setIsEditMode(true);
			}
		},
		[isValid]
	);

	const exitEditModeHandler = useCallback(
		(): void => {
			if (isValid) {
				setIsEditMode(false);
			}
		},
		[isValid]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			onItemReorder();

			const itemsCopy: ScopeItemFormModel[] = [...scopes];
			const item: ScopeItemFormModel = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			attachable
				.for("groupData")
				.for("scopes")
				.reducer
				.setValue(itemsCopy);
		},
		[
			onItemReorder,
			scopes,
			attachable
		]
	);

	const keyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Enter:
					nameAttachable.validation
						.triggerInputFinished();

					exitEditModeHandler();
					return true;
				default:
					return false;
			}
		},
		[
			exitEditModeHandler,
			nameAttachable.validation
		]
	);

	const switchToEditModeHandler = useCallback(
		(): void => {
			setIsEditMode(true);
			doAutoFocus();
		},
		[doAutoFocus]
	);

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			const activeItem: ScopeItemFormModel = ensureNotNil(scopes[activeIndex]);

			return getItemName(activeItem, scopesData);
		},
		[scopes, scopesData]
	);

	return (
		<>
			<ReorderableList.Group
				headerContent={(
					<ItemContent
						icon={(
							<Icon value="layer-group"/>
						)}
					>
						{isEditMode
							? (
								<div className={styles.input}>
									<TextInput
										{...nameAttachable.attachToTextInput()
											.withOnBlur(exitEditModeHandler)
										}
										attachedOn="right"
										trackingId="edit-group-name"
										autoFocus={autoFocus}
										onKeyDown={keyDownHandler}
										placeholder={localize("_EnterGroupName")}
									/>
								</div>
							)
							: (
								<div
									{...setTrackingId("group-item-name", `|${nameAttachable.reducer.path}`)}
									className={styles.clickable}
									onClick={switchToEditModeHandler}
								>
									{name}
								</div>
							)
						}
					</ItemContent>
				)}
				isContentInteractable
				validationMessage={errorToShow}
				actions={(
					<IconButton
						ref={menuButtonRef}
						icon="ellipsis"
						kind="transparent"
						trackingId="open-actions"
						shape="round"
						onClick={() => onOpenMoreMenu()}
						isActive={menuState.isOpen && !menuState.isContextMenu}
					/>
				)}
				trackingId={generateTrackingId("global-search-group-item", `|${groupItemClientId}`)}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu,
					draggableMode: isEditMode
						? "handle"
						: "header"
				}}
			>
				{scopes.length > 0 && (
					<ReorderableList
						onItemMove={isSearching ? undefined : itemMoveHandler}
						getOverlayText={getOverlayTextHandler}
						trackingId={generateTrackingId("global-search-item-list", `|${groupItemClientId}`)}
					>
						{attachable
							.for("groupData")
							.for("scopes")
							.map(x => (
								x.when(
									doesMatchSearchTerm,
									scopeAttachable => (
										<ScopeItem
											key={getClientId(scopeAttachable.value)}
											attachable={scopeAttachable}
											groups={groups}
											containingGroupId={groupItemClientId}
											onMoveToGroup={onMoveToGroup}
											onMoveToTop={onMoveToTop}
											scopesData={scopesData}
										/>
									)
								)
							))
						}
					</ReorderableList>
				)}
			</ReorderableList.Group>
			{menuState.isOpen && (
				<GroupItemMenu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					name={name}
					onDelete={() => onDeleteGroup(groupItemClientId)}
				/>
			)}
		</>
	);
};
GroupItem.displayName = "GroupItem";